const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Delay before autoplay resumes after a manual interaction (arrow/dot click). */
const RESUME_AFTER_INTERACTION_MS = 4000;

class CustomIconWithTextControls extends HTMLElement {
  #initialized = false;
  #resizeQueued = false;
  /** @type {number | undefined} */
  #autoplayTimer;
  /** @type {number | undefined} */
  #resumeTimer;
  #hoverPaused = false;
  #interactionPaused = false;
  /** @type {(HTMLElement & { refs?: any, slides?: HTMLElement[], current?: number, select: (input: number|string|{id: string}, event?: Event, options?: { animate?: boolean }) => void }) | null} */
  #slideshow = null;
  /** @type {HTMLElement | null} */
  #dots = null;

  connectedCallback() {
    requestAnimationFrame(this.#initialize);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSlideshowSelect);
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    REDUCED_MOTION_QUERY.removeEventListener('change', this.#handleReducedMotionChange);
    this.#clearAutoplayTimer();
    this.#initialized = false;
  }

  #initialize = () => {
    if (!this.isConnected || this.#initialized) return;

    this.#slideshow = this.closest('slideshow-component');
    this.#dots = this.querySelector('[data-page-dots]');

    if (!this.#slideshow || !this.#dots || !(this.#slideshow.refs?.scroller instanceof HTMLElement)) {
      requestAnimationFrame(this.#initialize);
      return;
    }

    this.#initialized = true;
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.#slideshow.addEventListener('slideshow:select', this.#handleSlideshowSelect);
    window.addEventListener('resize', this.#handleResize);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    REDUCED_MOTION_QUERY.addEventListener('change', this.#handleReducedMotionChange);

    this.#renderDots();
    this.#syncControls();
    this.#startAutoplay();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action], [data-page-index]') : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();

    if (target.dataset.pageIndex) {
      this.#goToPage(Number(target.dataset.pageIndex));
      this.#registerInteraction();
      return;
    }

    const currentPageIndex = this.#getCurrentPageIndex();
    if (target.dataset.action === 'previous') {
      this.#goToPage(currentPageIndex - 1, { wrap: true });
      this.#registerInteraction();
      return;
    }

    if (target.dataset.action === 'next') {
      this.#goToPage(currentPageIndex + 1, { wrap: true });
      this.#registerInteraction();
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    // Ignore touch/pen pointers so tapping doesn't get treated as a hover-hold.
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#hoverPaused = true;
    this.#stopAutoplay();
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#hoverPaused = false;
    this.#startAutoplay();
  };

  #handleFocusIn = () => {
    this.#hoverPaused = true;
    this.#stopAutoplay();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#hoverPaused = false;
    this.#startAutoplay();
  };

  #handleResize = () => {
    if (this.#resizeQueued) return;

    this.#resizeQueued = true;
    requestAnimationFrame(() => {
      this.#resizeQueued = false;

      const currentPageIndex = this.#getCurrentPageIndex();
      this.#renderDots();
      this.#goToPage(currentPageIndex, { animate: false });
      this.#syncControls();
    });
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopAutoplay();
      return;
    }

    this.#startAutoplay();
  };

  #handleReducedMotionChange = () => {
    this.#startAutoplay();
  };

  #handleSlideshowSelect = () => {
    this.#syncControls();
  };

  /**
   * @param {string} name
   * @param {number} fallback
   */
  #getNumberAttribute(name, fallback) {
    const value = Number(this.dataset[name]);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  #getSlides() {
    return Array.isArray(this.#slideshow?.slides) ? this.#slideshow.slides : [];
  }

  #getItemsPerPage() {
    if (window.innerWidth >= 990) return this.#getNumberAttribute('desktopColumns', 6);
    if (window.innerWidth >= 750) return this.#getNumberAttribute('tabletColumns', 4);
    return this.#getNumberAttribute('mobileColumns', 3);
  }

  #getPageStartIndexes() {
    const itemsPerPage = this.#getItemsPerPage();
    const totalItems = this.#getSlides().length || this.#getNumberAttribute('totalItems', 0);

    if (!itemsPerPage || !totalItems) return [];

    return Array.from({ length: Math.ceil(totalItems / itemsPerPage) }, (_, index) => index * itemsPerPage);
  }

  #getPageCount() {
    return this.#getPageStartIndexes().length;
  }

  /**
   * Derives the current page from the slideshow's own current slide index,
   * which is kept in sync by the base slideshow component (via IntersectionObserver
   * on scroll, drag, and programmatic select). This avoids re-deriving position from
   * scroll pixel offsets, which can drift out of sync with the actual visible slide.
   */
  #getCurrentPageIndex() {
    const pageStartIndexes = this.#getPageStartIndexes();
    if (!pageStartIndexes.length) return 0;

    const currentSlideIndex = this.#slideshow?.current ?? 0;

    let closestPageIndex = 0;
    let smallestDistance = Infinity;

    pageStartIndexes.forEach((startIndex, pageIndex) => {
      const distance = Math.abs(startIndex - currentSlideIndex);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestPageIndex = pageIndex;
      }
    });

    return closestPageIndex;
  }

  #renderDots() {
    if (!this.#dots) return;

    const pageCount = this.#getPageCount();
    this.#dots.hidden = pageCount <= 1;

    if (this.#dots.childElementCount === pageCount) return;

    this.#dots.replaceChildren();

    const fragment = document.createDocumentFragment();

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const text = document.createElement('span');

      button.type = 'button';
      button.className = 'button button-unstyled';
      button.dataset.pageIndex = String(pageIndex);
      button.setAttribute('aria-label', `Go to icon page ${pageIndex + 1} of ${pageCount}`);
      button.setAttribute('aria-selected', pageIndex === 0 ? 'true' : 'false');

      text.className = 'visually-hidden';
      text.textContent = `Page ${pageIndex + 1}`;

      button.append(text);
      item.append(button);
      fragment.append(item);
    }

    this.#dots.append(fragment);
  }

  #syncControls() {
    const currentPageIndex = this.#getCurrentPageIndex();
    const pageCount = this.#getPageCount();

    this.#dots?.querySelectorAll('[data-page-index]').forEach((button, index) => {
      /** @type {Element} */ (button).setAttribute('aria-selected', index === currentPageIndex ? 'true' : 'false');
    });

    const previous = this.querySelector('[data-action="previous"]');
    const next = this.querySelector('[data-action="next"]');

    // Navigation always wraps (previous/next loop around), so arrows stay
    // enabled whenever there is more than one page rather than disabling at the ends.
    if (previous instanceof HTMLButtonElement) {
      previous.disabled = pageCount <= 1;
      previous.hidden = pageCount <= 1;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = pageCount <= 1;
      next.hidden = pageCount <= 1;
    }
  }

  /**
   * @param {number} pageIndex
   * @param {{ animate?: boolean, wrap?: boolean }} [options]
   */
  #goToPage(pageIndex, options = {}) {
    const { animate = true, wrap = false } = options;
    const slides = this.#getSlides();
    const pageStartIndexes = this.#getPageStartIndexes();

    if (!slides.length || !pageStartIndexes.length || !this.#slideshow) return;

    let targetPageIndex = pageIndex;

    if (wrap) {
      if (targetPageIndex < 0) targetPageIndex = pageStartIndexes.length - 1;
      if (targetPageIndex >= pageStartIndexes.length) targetPageIndex = 0;
    } else {
      targetPageIndex = Math.max(0, Math.min(targetPageIndex, pageStartIndexes.length - 1));
    }

    const targetSlideIndex = pageStartIndexes[targetPageIndex];
    if (targetSlideIndex == null) return;

    this.#slideshow.select(targetSlideIndex, undefined, { animate });

    requestAnimationFrame(() => {
      this.#syncControls();
    });
  }

  #shouldAutoplay() {
    return (
      this.dataset.autoplay === 'true' &&
      !document.hidden &&
      !this.#hoverPaused &&
      !this.#interactionPaused &&
      !REDUCED_MOTION_QUERY.matches &&
      this.#getPageCount() > 1
    );
  }

  #getAutoplayDelay() {
    return this.#getNumberAttribute('autoplaySpeed', 5) * 1000;
  }

  #clearAutoplayTimer() {
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  #stopAutoplay() {
    this.#clearAutoplayTimer();
  }

  #startAutoplay() {
    this.#clearAutoplayTimer();
    if (!this.#shouldAutoplay()) return;

    this.#autoplayTimer = window.setInterval(() => {
      this.#goToPage(this.#getCurrentPageIndex() + 1, { wrap: true });
    }, this.#getAutoplayDelay());
  }

  /**
   * Called when the user manually changes slides via an arrow or dot click.
   * Pauses autoplay, then resumes it automatically after a short idle period.
   */
  #registerInteraction() {
    this.#interactionPaused = true;
    this.#stopAutoplay();

    window.clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      this.#interactionPaused = false;
      this.#startAutoplay();
    }, RESUME_AFTER_INTERACTION_MS);
  }
}

if (!customElements.get('custom-icon-with-text-controls')) {
  customElements.define('custom-icon-with-text-controls', CustomIconWithTextControls);
}
